import { useEffect, useState, useRef } from 'react';
import { Target, Linkedin, Users, Sparkles, Shield, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '../hooks/useScrollReveal';
import Footer from './Footer';
import { Logos3 } from '@/components/ui/logos3';

const leaders = [
    {
        name: "Sashank Pappu",
        role: "Chief Executive Officer",
        linkedin: "https://www.linkedin.com/in/sashank-pappu/",
        bio: "A visionary leader dedicated to architecting the next generation of autonomous enterprise systems. Sashank leads Antz with a focus on collective intelligence and strategic growth.",
        image: "/CEO.png"
    },
    {
        name: "Priya Peri",
        role: "COO & Co-Founder",
        linkedin: "https://www.linkedin.com/in/priya-peri-96a46938b/",
        bio: "A strategic operator driving the business engine at Antz. Priya co-founded the company and oversees operations, ensuring seamless execution from strategy to delivery.",
        image: "/Priya-peri.jpeg"
    },
    {
        name: "Siddarth Amaravadi",
        role: "Chief Technology Officer",
        linkedin: "https://www.linkedin.com/in/siddharth-amaravadi-32438b211/",
        bio: "A technologist at heart, Siddarth is the driving force behind the cognitive architecture at Antz. He specializes in engineering resilient, adaptive systems.",
        image: "/SiddarthAmaravadiimage.jpeg"
    }
];

const pillars = [
    {
        icon: Shield,
        title: "Resilience",
        description: "Like our namesake, we build systems that adapt, recover, and thrive in dynamic environments."
    },
    {
        icon: Target,
        title: "Strategy",
        description: "Every action is calculated. We plan for the long term, optimizing for efficiency and scale."
    },
    {
        icon: Users,
        title: "Collective Intelligence",
        description: "Individual agents are powerful; together, they are unstoppable. We harness the power of the swarm."
    },
    {
        icon: Zap,
        title: "Hard Work",
        description: "We believe in the compound effect of consistent, high-quality effort applied to complex problems."
    }
];

const AnimatedCounter = ({ target, suffix = '' }: { target: string; suffix?: string }) => {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLSpanElement>(null);
    const started = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started.current) {
                    started.current = true;
                    const num = parseInt(target);
                    const duration = 1500;
                    const step = Math.ceil(duration / num);
                    let current = 0;
                    const timer = setInterval(() => {
                        current += 1;
                        setCount(current);
                        if (current >= num) clearInterval(timer);
                    }, step);
                }
            },
            { threshold: 0.5 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [target]);

    return <span ref={ref}>{count}{suffix}</span>;
};

const PillarCard = ({ pillar, index }: { pillar: typeof pillars[0]; index: number }) => {
    const { ref, isVisible } = useScrollReveal();
    const Icon = pillar.icon;

    return (
        <div
            ref={ref}
            className={`group p-6 rounded-xl border border-overlay/[0.06] bg-overlay/[0.02] hover:border-overlay/[0.1] transition-all duration-300 ${isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 80}ms` }}
        >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-2">{pillar.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
        </div>
    );
};

const LeaderCard = ({ leader, index }: { leader: typeof leaders[0]; index: number }) => {
    const { ref, isVisible } = useScrollReveal();

    return (
        <div
            ref={ref}
            className={`group rounded-xl border border-overlay/[0.06] bg-overlay/[0.02] overflow-hidden hover:border-overlay/[0.1] transition-all duration-300 ${isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 100}ms` }}
        >
            {/* Photo */}
            <div className="aspect-[4/3] bg-overlay/[0.03] overflow-hidden">
                <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                    onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                    }}
                />
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 items-center justify-center hidden">
                    <span className="text-4xl font-semibold text-primary/60">
                        {leader.name.split(' ').map(n => n[0]).join('')}
                    </span>
                </div>
            </div>

            {/* Info */}
            <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="text-lg font-semibold text-foreground">{leader.name}</h3>
                        <p className="text-sm text-primary">{leader.role}</p>
                    </div>
                    <a
                        href={leader.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg border border-overlay/[0.08] flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-overlay/[0.15] transition-colors shrink-0"
                    >
                        <Linkedin className="w-3.5 h-3.5" />
                    </a>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{leader.bio}</p>
            </div>
        </div>
    );
};

const AboutUsPage = () => {
    const navigate = useNavigate();
    const [visibleSections, setVisibleSections] = useState(new Set<string>());
    const sectionsRef = useRef<(HTMLElement | null)[]>([]);
    const pillarsHeader = useScrollReveal();
    const teamHeader = useScrollReveal();
    const cultureSection = useScrollReveal();
    const logosSection = useScrollReveal();
    const ctaSection = useScrollReveal();

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const target = entry.target as HTMLElement;
                        setVisibleSections((prev) => new Set([...prev, target.dataset.section || '']));
                    }
                });
            },
            { threshold: 0.1 }
        );
        sectionsRef.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const addRef = (i: number) => (el: HTMLElement | null) => { sectionsRef.current[i] = el; };
    const isSectionVisible = (name: string) => visibleSections.has(name);

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
            <div className="pb-24">
                {/* Hero */}
                <section className="max-w-[1200px] mx-auto pt-44 px-8 md:pt-36">
                    <div className="max-w-[720px] animate-about-fade-up">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 pr-4 pl-2.5 border border-border rounded-full text-sm text-muted-foreground mb-8 animate-about-fade-in [animation-delay:0.4s]">
                            <span className="w-[7px] h-[7px] bg-muted-foreground/50 rounded-full animate-about-pulse" />
                            <span>About Our Team</span>
                        </div>
                        <h1 className="font-heading text-[clamp(2.8rem,5.5vw,4.5rem)] font-normal leading-[1.1] tracking-[-1.5px] text-foreground mb-6" style={{ textWrap: 'balance' }}>
                            Building software that <span className="font-semibold italic">gets the work done</span>
                        </h1>
                        <p className="font-sans text-lg leading-[1.7] text-muted-foreground max-w-[520px] animate-about-fade-up [animation-delay:0.5s]" style={{ textWrap: 'balance' }}>
                            We design AI systems, automations, and enterprise solutions that don’t just assist teams — they help businesses execute faster, operate leaner, and scale with confidence.
                        </p>
                    </div>
                    <div className="mt-14 relative animate-about-image-in [animation-delay:0.6s] about-hero-image-wrap">
                        <img src="/team.png" alt="Our team" className="w-full block about-hero-image transition-all duration-800" />
                        <div className="absolute top-0 bottom-0 left-0 w-[15%] z-[2] pointer-events-none bg-gradient-to-r from-background to-transparent" />
                        <div className="absolute top-0 bottom-0 right-0 w-[15%] z-[2] pointer-events-none bg-gradient-to-l from-background to-transparent" />
                    </div>
                </section>

                {/* Stats */}
                <section
                    className={`max-w-[1200px] mx-auto py-20 px-8 border-b border-border`}
                    data-section="stats"
                    ref={addRef(0)}
                >
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { val: '50', suffix: '+', label: 'Projects Delivered' },
                            { val: '8', suffix: '+', label: 'Years of Experience' },
                            { val: '30', suffix: '+', label: 'Team Members' },
                            { val: '99', suffix: '%', label: 'Client Satisfaction' },
                        ].map((s, i) => (
                            <div
                                key={s.label}
                                className={`text-center transition-all duration-600 ${isSectionVisible('stats') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                                style={{ transitionDelay: `${i * 0.1}s` }}
                            >
                                <span className="block font-heading text-5xl font-normal tracking-[-1px] text-foreground">
                                    <AnimatedCounter target={s.val} suffix={s.suffix} />
                                </span>
                                <span className="block font-sans text-sm text-muted-foreground mt-1.5">{s.label}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Mission */}
                <section
                    className="max-w-[1200px] mx-auto py-24 px-8"
                    data-section="mission"
                    ref={addRef(1)}
                >
                    <div className={`max-w-[680px] transition-all duration-800 ${isSectionVisible('mission') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[30px]'}`}>
                        <span className="inline-block font-sans text-xs font-semibold tracking-[2px] uppercase text-muted-foreground mb-6">Our Mission</span>
                        <h2 className="font-heading text-[clamp(1.8rem,3.5vw,2.8rem)] font-normal leading-[1.25] tracking-[-0.5px] text-foreground mb-6" style={{ textWrap: 'balance' }}>
                            We believe software should deliver <span className="font-semibold italic">outcomes</span>, not just features.
                        </h2>
                        <p className="font-sans text-base leading-[1.8] text-muted-foreground">
                            Our mission is to build systems that reduce manual effort, streamline execution, and turn complex business processes into reliable, scalable workflows — while keeping humans in control where judgment matters most.
                        </p>
                    </div>
                </section>

                {/* Pillars */}
                <section className="container mx-auto px-4 mb-24 section-glow">
                    <div
                        ref={pillarsHeader.ref}
                        className={`text-center mb-14 max-w-2xl mx-auto ${pillarsHeader.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                    >
                        <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-[-0.04em]">Inspired by nature</h2>
                        <p className="text-sm text-muted-foreground">
                            Drawing inspiration from the humblest of beings, we find that ants embody values that drive our engineering culture.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                        {pillars.map((pillar, i) => (
                            <PillarCard key={i} pillar={pillar} index={i} />
                        ))}
                    </div>
                </section>

                {/* Leadership */}
                <section className="container mx-auto px-4 mb-24 section-glow">
                    <div
                        ref={teamHeader.ref}
                        className={`text-center mb-14 max-w-2xl mx-auto ${teamHeader.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                    >
                        <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-[-0.04em]">Our leadership</h2>
                        <p className="text-sm text-muted-foreground">
                            Meet the team behind Antz, dedicated to revolutionizing how enterprises operate through collective intelligence.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {leaders.map((leader, i) => (
                            <LeaderCard key={i} leader={leader} index={i} />
                        ))}
                    </div>
                </section>

                {/* Client Logos */}
                <div
                    ref={logosSection.ref}
                    className={logosSection.isVisible ? 'animate-reveal-up' : 'opacity-0'}
                >
                    <Logos3 heading="Trusted by industry leaders" />
                </div>

                {/* Culture */}
                <section className="container mx-auto px-4 mb-24">
                    <div
                        ref={cultureSection.ref}
                        className={`max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${cultureSection.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                    >
                        <div>
                            <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-[-0.04em]">Winning as a team</h2>
                            <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
                                At Antz, we draw inspiration from the remarkable world of ants — where collaboration and unity lead to extraordinary achievements. Just as ants work together seamlessly to overcome challenges, we empower businesses to harness the collective intelligence of AI.
                            </p>
                            <div className="space-y-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <Users className="w-4 h-4 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-foreground mb-1">Collaborative Spirit</h4>
                                        <p className="text-sm text-muted-foreground">Fostering a culture where every voice contributes to the solution.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <Sparkles className="w-4 h-4 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-foreground mb-1">Excellence in Execution</h4>
                                        <p className="text-sm text-muted-foreground">Crafting high-quality products that really matter.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 rounded-xl border border-overlay/[0.06] bg-overlay/[0.02]">
                            <blockquote className="text-lg font-light italic text-foreground mb-6 leading-relaxed">
                                "Antz created a place where people can work in unison, craft high-quality products, and do things that really matter."
                            </blockquote>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Users className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-foreground">The Antz Team</div>
                                    <div className="text-xs text-muted-foreground">Since Day 1</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="container mx-auto px-4">
                    <div
                        ref={ctaSection.ref}
                        className={`max-w-3xl mx-auto text-center p-12 rounded-xl border border-overlay/[0.06] bg-overlay/[0.02] ${ctaSection.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                    >
                        <h2 className="text-2xl md:text-3xl font-semibold mb-4 tracking-[-0.04em]">Ready to join the swarm?</h2>
                        <p className="text-sm text-muted-foreground mb-8 max-w-xl mx-auto">
                            We are always looking for resilient, strategic thinkers to join our mission.
                        </p>
                        <div className="flex flex-col md:flex-row gap-3 justify-center">
                            <button
                                onClick={() => navigate('/careers')}
                                className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity shadow-[0_0_24px_rgba(124,58,237,0.25)]"
                            >
                                View Open Positions
                            </button>
                            <button
                                onClick={() => navigate('/contact-us')}
                                className="bg-transparent text-foreground px-8 py-3 rounded-lg font-medium text-sm hover:bg-overlay/[0.05] transition-colors border border-overlay/[0.1]"
                            >
                                Contact Us
                            </button>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default AboutUsPage;
